import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useTenant } from '@/hooks/useTenant';
import { useVisibility } from '@/hooks/useVisibility';
import type { ServiceType, Tech, LeadSource, PartsCatalog, ServiceCatalog } from '@/types';

export type TechWithMetros = Tech & {
  tech_metros: { metro_id: string }[];
};

// Each variant keeps its own literal select: the Supabase select-string types
// cannot parse a union of two column lists.
const TECH_COLUMNS =
  'id, company_id, name, email, phone, is_active, user_id, contract_signed, contract_signed_date, notes, owns_tools, pays_for_vehicle, tools_purchased_date, created_at';
const TECH_COLUMNS_WITH_PAY =
  'id, company_id, name, email, phone, is_active, user_id, commission_percentage, contract_signed, contract_signed_date, notes, owns_tools, pays_for_vehicle, tools_purchased_date, created_at';
const TECH_COLUMNS_WITH_METROS =
  'id, company_id, name, email, phone, is_active, user_id, contract_signed, contract_signed_date, notes, owns_tools, pays_for_vehicle, tools_purchased_date, created_at, tech_metros(metro_id)';
const TECH_COLUMNS_WITH_PAY_AND_METROS =
  'id, company_id, name, email, phone, is_active, user_id, commission_percentage, contract_signed, contract_signed_date, notes, owns_tools, pays_for_vehicle, tools_purchased_date, created_at, tech_metros(metro_id)';

export function useServiceTypes(activeOnly = true) {
  return useQuery({
    queryKey: ['service-types', activeOnly],
    queryFn: async () => {
      let query = supabase
        .from('service_types')
        .select('id, company_id, name, description, display_order, is_active, created_at')
        .order('display_order');
      if (activeOnly) query = query.eq('is_active', true);
      const { data, error } = await query;
      if (error) throw error;
      return data as ServiceType[];
    },
  });
}

export function useTechs(activeOnly = true) {
  const { canSeeTechPay } = useVisibility();
  return useQuery({
    queryKey: ['techs', activeOnly, canSeeTechPay],
    queryFn: async () => {
      if (canSeeTechPay) {
        let query = supabase.from('techs').select(TECH_COLUMNS_WITH_PAY).order('name');
        if (activeOnly) query = query.eq('is_active', true);
        const { data, error } = await query;
        if (error) throw error;
        return data as Tech[];
      }
      let query = supabase.from('techs').select(TECH_COLUMNS).order('name');
      if (activeOnly) query = query.eq('is_active', true);
      const { data, error } = await query;
      if (error) throw error;
      return data as Tech[];
    },
  });
}

export interface TechUser {
  id: string;
  name: string;
}

/**
 * Technicians that hold a Steddy app login, keyed by auth user id. Estimates,
 * payments and invoices created from the app carry that user id in created_by /
 * recorded_by, which is how the CRM marks "from the field".
 */
export function useTechUsers() {
  return useQuery({
    queryKey: ['techs', 'users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('techs')
        .select('id, name, user_id')
        .not('user_id', 'is', null);
      if (error) throw error;
      return new Map<string, TechUser>((data ?? []).map((t) => [t.user_id as string, { id: t.id, name: t.name }]));
    },
    staleTime: 5 * 60_000,
  });
}

export function useTechsWithMetros(activeOnly = true) {
  const { canSeeTechPay } = useVisibility();
  return useQuery({
    queryKey: ['techs-with-metros', activeOnly, canSeeTechPay],
    queryFn: async () => {
      if (canSeeTechPay) {
        let query = supabase.from('techs').select(TECH_COLUMNS_WITH_PAY_AND_METROS).order('name');
        if (activeOnly) query = query.eq('is_active', true);
        const { data, error } = await query;
        if (error) throw error;
        return (data ?? []) as TechWithMetros[];
      }
      let query = supabase.from('techs').select(TECH_COLUMNS_WITH_METROS).order('name');
      if (activeOnly) query = query.eq('is_active', true);
      const { data, error } = await query;
      if (error) throw error;
      return (data ?? []) as TechWithMetros[];
    },
  });
}

/**
 * @deprecated metro_postcode_ranges was dropped (migration 20260419142512_remove_postcode_ranges);
 * metros are assigned by DB triggers via the haversine radius system. Always resolves null.
 */
export function useMetroFromPostcode(postcode: string | undefined) {
  return useQuery({
    queryKey: ['metro-from-postcode', postcode],
    queryFn: async () => null as string | null,
    enabled: !!postcode && /^\d{4}$/.test(postcode ?? ''),
  });
}

/**
 * Returns active techs eligible for a given metro.
 * Rule: a tech with NO metro assignments is selectable everywhere (zero-disruption default).
 * A tech with assignments is only selectable in their assigned metros.
 */
export function useTechsByMetro(metroId: string | null | undefined) {
  const { data: allTechs = [], isLoading } = useTechsWithMetros(true);

  const eligible = useMemo(() => {
    if (!metroId) return allTechs;
    return allTechs.filter(tech => {
      const assignments = tech.tech_metros ?? [];
      if (assignments.length === 0) return true;
      return assignments.some(a => a.metro_id === metroId);
    });
  }, [allTechs, metroId]);

  return { data: eligible, isLoading };
}

export function useLeadSources(activeOnly = true) {
  return useQuery({
    queryKey: ['lead-sources', activeOnly],
    queryFn: async () => {
      let query = supabase
        .from('lead_sources')
        .select('id, company_id, brand_id, name, is_active, created_at')
        .order('name');
      if (activeOnly) query = query.eq('is_active', true);
      const { data, error } = await query;
      if (error) throw error;
      return data as LeadSource[];
    },
  });
}

export function usePartsCatalog(activeOnly = true) {
  return useQuery({
    queryKey: ['parts-catalog', activeOnly],
    queryFn: async () => {
      let query = supabase
        .from('parts_catalog')
        .select('id, company_id, name, default_cost, default_sell_price, is_active, created_at')
        .order('name');
      if (activeOnly) query = query.eq('is_active', true);
      const { data, error } = await query;
      if (error) throw error;
      return data as PartsCatalog[];
    },
  });
}

export function useServiceCatalog(activeOnly = true) {
  return useQuery({
    queryKey: ['service-catalog', activeOnly],
    queryFn: async () => {
      let query = supabase
        .from('service_catalog')
        .select('id, company_id, name, description, default_price, is_active, created_at')
        .order('name');
      if (activeOnly) query = query.eq('is_active', true);
      const { data, error } = await query;
      if (error) throw error;
      return data as ServiceCatalog[];
    },
  });
}

export function useCsrProfiles() {
  const { tenantId } = useTenant();

  return useQuery({
    queryKey: ['csr-profiles', tenantId],
    enabled: !!tenantId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const { data: csrRoles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'csr')
        .eq('company_id', tenantId!);
      if (rolesError) throw rolesError;
      const csrUserIds = (csrRoles || []).map(r => r.user_id);
      if (csrUserIds.length === 0) return [];

      const { data, error } = await supabase
        .from('profiles')
        .select('id, full_name, email')
        .in('id', csrUserIds)
        .eq('company_id', tenantId!)
        .order('full_name');
      if (error) throw error;
      return data;
    },
  });
}

export function useCSRs() {
  return useCsrProfiles();
}

export function useMetros(activeOnly = true) {
  return useQuery({
    queryKey: ['metros', activeOnly],
    queryFn: async () => {
      let query = supabase
        .from('metros')
        .select('id, company_id, name, state, description, display_order, is_active, created_at, center_lat, center_lng, radius_km')
        .order('name');
      if (activeOnly) query = query.eq('is_active', true);
      const { data, error } = await query;
      if (error) throw error;
      return data || [];
    },
  });
}

export { useCampaigns } from './useCampaigns';
